import { useCallback, useMemo } from 'react';

import { useIntl } from 'react-intl';
import { Link } from 'react-router-dom';

import Grid from '@mui/material/Grid2';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import DeleteForeverOutlinedIcon from '@mui/icons-material/DeleteForeverOutlined';

import {
  useDeleteFieldCategory,
  useGetFieldCategories,
} from 'src/pages/SetupPage/api';
import DataTable from 'src/components/DataTable';
import ConfirmationDialog from 'src/components/ConfirmationDialog';

import messages from './messages';
import { FieldCategoryForm } from './components/FieldCategoryForm';

export default function FieldCategories() {
  const { formatMessage } = useIntl();

  const { mutate: deleteFieldCategory } = useDeleteFieldCategory();
  const { data: fieldCategories, isFetching: isFetchingFieldCategories } =
    useGetFieldCategories();

  const columns: Array<{
    id: keyof UCM.FieldCategoryType | string;
    label: string | React.ReactNode;
  }> = useMemo(() => {
    return [
      {
        id: 'name_en',
        label: formatMessage(messages.titleEnColumnLabel),
        render: (_: Object, category: UCM.FieldCategoryType) => (
          <Link to={`/categories/${category.uuid}/dynamic_fields`}>
            <Typography variant="inherit">{category.name.en}</Typography>
          </Link>
        ),
      },
      {
        id: 'name_de',
        label: formatMessage(messages.titleDeColumnLabel),
        render: (_: Object, category: UCM.FieldCategoryType) => (
          <Typography variant="inherit">{category.name.de ?? '-'}</Typography>
        ),
      },
      {
        id: 'position',
        align: 'center',
        label: formatMessage(messages.positionColumnLabel),
        render: (position: number) => (
          <Typography variant="inherit">{position}</Typography>
        ),
      },
      {
        id: 'actions',
        label: '',
        align: 'center',
        width: 0,
        render: (_: Object, category: UCM.FieldCategoryType) => (
          <Grid display="flex" alignItems="center">
            <FieldCategoryForm create={false} fieldCategory={category} />

            <ConfirmationDialog
              onConfirm={() => handleDelete(category.uuid)}
              message={formatMessage(messages.deleteCategoryConfirmation)}
            >
              {(handleOpen) => (
                <Tooltip arrow title={formatMessage(messages.deleteTooltip)}>
                  <IconButton onClick={handleOpen}>
                    <DeleteForeverOutlinedIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
            </ConfirmationDialog>
          </Grid>
        ),
      },
    ];
  }, []);

  const handleDelete = useCallback((categoryUuid: string) => {
    deleteFieldCategory(categoryUuid);
  }, []);

  return (
    <Grid container spacing={2} size={12}>
      <Grid
        size={12}
        display="flex"
        alignItems="center"
        justifyContent="flex-end"
      >
        <FieldCategoryForm create={true} />
      </Grid>

      <Grid size={12}>
        <DataTable
          columns={columns}
          enabledFooter={false}
          rows={fieldCategories}
          loading={isFetchingFieldCategories}
        />
      </Grid>
    </Grid>
  );
}
